<template>
  <div>
    <el-form
      ref="settlementForm"
      :model="settlement"
      class="box"
      label-width="100px"
      v-if="settlement != null"
    >
      <el-row>
        <el-col :span="8">
          <el-form-item :label="$t('settlement.title')">{{
            settlement.title
          }}</el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item :label="$t('settlement.settlerName')">{{
            settlement.settlerName
          }}</el-form-item>
        </el-col>
      </el-row>
    </el-form>
    <fieldset class="mgb15">
      <legend>
        {{ $t("text.lineItem") }}
        <el-button type="default" size="mini" @click="download">
          {{ $t("action.download") }}
        </el-button>
      </legend>
      <vxe-table
        :data="items"
        :loading="loading"
        ref="itemsTable"
        class="recordsTable"
        size="mini"
        highlight-hover-row
        :scroll-y="{ enabled: false }"
      >
        <vxe-table-column field="total" :title="$t('payment.total')">
          <template v-slot="{ row }">
            <currency :code="row.currency"></currency>
            {{ row.total }}
            <div class="font10">{{ row.cnyTotal | currency }}</div>
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="payMethodName"
          :title="$t('payment.payMethod')"
        ></vxe-table-column>
        <vxe-table-column
          field="paidAt"
          :title="$t('payment.paidAt')"
          width="110px"
        ></vxe-table-column>
        <vxe-table-column
          field="ownerName"
          :title="$t('payment.ownerName')"
        ></vxe-table-column>
        <vxe-table-column field="voucher" :title="$t('payment.voucher')">
          <template v-slot="{ row }">
            <thumbnail-text :srcs="row.voucher" />
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="explain"
          :title="$t('payment.explain')"
        ></vxe-table-column>
        <vxe-table-column field="row.bargain" :title="$t('bargain.serialNo')">
          <template v-slot="{ row }">
            <el-popover
              placement="right"
              width="800"
              trigger="click"
              @show="getCurrentRowOrder(row)"
            >
              <vxe-table
                :data="currentRowOrders"
                ref="itemsTable"
                class="recordsTable"
                size="mini"
                :scroll-y="{ enabled: false }"
                highlight-hover-row
              >
                <vxe-table-column
                  field="images"
                  :title="$t('product.images')"
                  width="85"
                >
                  <template v-slot="{ row }">
                    <thumbnail :images="row.images"></thumbnail>
                  </template>
                </vxe-table-column>
                <vxe-table-column
                  field="serialNo"
                  :title="$t('text.serialNo')"
                  min-width="100"
                >
                  <template v-slot="{ row }">
                    <div @click="showOrder(row)" class="blue">
                      {{ row.serialNo }}
                    </div>
                    <div>
                      <state-label :state="row.workflowState">{{
                        $t("orderStates." + row.workflowState)
                      }}</state-label>
                    </div>
                  </template>
                </vxe-table-column>

                <vxe-table-column
                  field="createAt"
                  :title="$t('text.createAt')"
                  min-width="90"
                >
                  <template v-slot="{ row }">
                    {{ row.createAt }}
                    <div class="font10">
                      {{ $t("text.handOverAt2") }}:{{ row.handOverAt }}
                    </div>
                  </template>
                </vxe-table-column>

                <vxe-table-column
                  field="finishAt"
                  :title="$t('rework.finishAt')"
                  min-width="150"
                >
                  <template v-slot="{ row }">
                    <div class="font10">
                      {{ $t("order.finishAt") }}:{{ row.finishAt }}
                    </div>
                    <div class="font10">
                      {{ $t("order.customerReceiveAt2") }}:{{
                        row.customerReceiveAt
                      }}
                    </div>
                  </template>
                </vxe-table-column>

                <vxe-table-column
                  field="productNo"
                  :title="$t('product.productNo')"
                  min-width="100"
                >
                  <template v-slot="{ row }">
                    {{ row.productNo }}
                    <div class="font10">{{ row.color }} {{ row.size }}</div>
                  </template>
                </vxe-table-column>
                <vxe-table-column
                  field="quantity"
                  :title="$t('text.quantity')"
                  min-width="100"
                >
                  <template v-slot="{ row }">
                    {{ row.quantity }}{{ row.unit }}
                    <div class="font10">
                      {{ $t("order.delivered") }}: {{ row.deliveredQuantity
                      }}{{ row.unit }}
                    </div>
                    <div class="font10">
                      {{ $t("order.price") }}:<currency
                        :code="row.currency"
                      ></currency>
                      {{ row.price }}
                    </div>
                    <div
                      v-if="row.extraCharge != null && row.extraCharge != 0"
                      class="font10"
                    >
                      {{ $t("order.extraCharge") }}:<currency
                        :code="row.currency"
                      ></currency>
                      {{ row.extraCharge }}
                    </div>
                  </template>
                </vxe-table-column>
                <vxe-table-column :title="$t('order.subtotal')" min-width="80">
                  <template v-slot="{ row }">
                    <currency :code="row.currency"></currency>
                    {{ row.price * row.quantity + row.extraCharge }}
                  </template>
                </vxe-table-column>

                <vxe-table-column
                  :title="$t('order.settlementRemark')"
                  min-width="80"
                >
                  <template v-slot="{ row }">
                    <raw-html-limit-number
                      :content="row.settlementRemark"
                      :number="8"
                    />
                  </template>
                </vxe-table-column>
              </vxe-table>
              <span slot="reference" class="popover-reference">
                {{ row.bargain.serialNo }}
              </span>
            </el-popover>
          </template>
        </vxe-table-column>
      </vxe-table>
    </fieldset>
    <div class="right pd20">
      <el-button size="small" type="default" @click="close">
        {{ $t("text.close") }}
      </el-button>
    </div>
  </div>
</template>

<script>
import { page } from "@/utils/mixins.js";
import Util from "@/utils/index.js";
import RawHtml from "@/components/RawHtml";
import ThumbnailText from "@/components/ThumbnailText";
import Currency from "@/components/Currency";
import SaleSettlement from "@/api/saleSettlement.js";
import SaleSettlementItem from "@/api/saleSettlementItem.js";
import Order from "@/api/order.js";

export default {
  props: {
    id: Number,
  },
  mixins: [page],
  components: {
    RawHtml,
    ThumbnailText,
    Currency,
  },
  data() {
    return {
      settlement: { settle: false },
      items: [],
      currentRowOrders: [],
      loading: false,
    };
  },
  watch: {
    id() {
      this.getSettlement();
    },
  },
  created() {
    const self = this;
    self.loadingStart();
    SaleSettlement.findById(self.id).then(function (res) {
      if (res.data.code === 200) {
        self.settlement = res.data.entity;
      } else {
        self.sysError();
      }
      self.loadingEnd();
      self.getItems();
    });
  },
  methods: {
    download() {
      const self = this;
      SaleSettlementItem.exportExcelBySaleSettlementId(self.id).then(function (
        res
      ) {
        if (res.status === 200) {
          self.$message({
            message: self.$t("prompt.downloadPrompt"),
            type: "success",
          });
          self.$eventHub.$emit("emitDownLoad");
        } else {
          self.$message.error(self.$t("prompt.downloadErr"));
        }
      });
    },
    getCurrentRowOrder(row) {
      const self = this;
      Order.findBySaleSettlementIdAndBarginId(
        self.settlement.id,
        row.bargainId
      ).then(function (res) {
        if (res.status == 200) {
          self.currentRowOrders = res.data;
        } else {
          self.sysError;
        }
      });
    },
    getItems() {
      const self = this;
      self.loading = true;
      SaleSettlementItem.findBySaleSettlementId(self.id).then(function (res) {
        if (res.status === 200) {
          self.items = res.data;
          self.loading = false;
        } else {
          self.sysError();
        }
      });
    },
    close() {
      this.$emit("close");
    },
  },
};
</script>
